import React from 'react'
import { t } from 'ttag'
import cx from 'classnames'
import { Box } from 'components/layout/Box/Box'
import { Description } from 'components/modals/TestsReportModal/common/Description/Description'
import { ScoreTag } from 'components/shared/ScoreTag/ScoreTag'
import { getIntro } from './../helpers'
import { TAB_NAMES } from '../../model'
import { DonutChart } from 'components/common/DonutChart/DonutChart'
import { useCompetencyState } from '../Competency/hooks'
import { IconTooltip } from 'components/shared/IconTooltip/IconTooltip'
import { CautionSign } from 'components/shared/CautionSign/CautionSign'
import { RadarChart } from 'components/common/RadarChart/RadarChart'
import { useIntroductionState } from './hooks'
import styles from './Introduction.module.scss'
import { factorsOverview } from './constants'

import tabStyles from '../tabs.module.scss'

const getSoreLabel = index => [t`Low`, t`Normal`, t`High`][index]

export const Introduction = ({
  language,
  scores,
  name,
  project,
  response,
  selectedCompetency,
  setSelectedCompetency,
  selectedCandidates,
  candidates,
  cautions = [],
  onTab,
}) => {
  const {
    state: { outcomeFactors, visibleCategories },
  } = useIntroductionState({
    project,
    language,
    response,
  })

  const {
    state: { showRadar, radarData },
    handlers: { getScoreLabel },
  } = useCompetencyState({
    project,
    language,
    response,
    selectedCompetency,
  })

  const single = candidates.length === 1

  // hidden until we make factor callculations better
  const noOutcomeFactors = true // outcomeFactors.every(({ score }) => isNaN(score))

  const noCategories =
    visibleCategories.length === 0 ||
    visibleCategories[0]?.length > 10 ||
    visibleCategories.every(category =>
      category.some(({ score }) => isNaN(score)),
    )

  return (
    <Box align="center" className={styles.introduction}>
      <Box className={styles.description} direction="column">
        <Box className={styles.header} justify="center">
          <div className={styles.titleRow}>
            <h3 className={tabStyles.tabTitle}>
              {t`Tests report`}: {`${name}`}
            </h3>
            <CautionSign count={cautions.length} placement="bottom" />
          </div>
          <div className={styles.subTitle}>{`for ${project.name}`}</div>
        </Box>

        <ScoreTag
          score={scores[0]['Ebber Test Score'] * 100}
          label={t`Ebber score`}
        />
        <Description text={getIntro(language)} />
        <div className={styles.scores}>
          <button
            className={styles.tagLink}
            type="button"
            onClick={() => onTab(TAB_NAMES.GMA)}
          >
            <ScoreTag
              className={styles.tag}
              score={scores[0]['GMA'] * 100}
              label={t`General Mental Ability`}
            />
          </button>
          <button
            className={styles.tagLink}
            type="button"
            onClick={() => onTab(TAB_NAMES.PERSONALITY)}
          >
            <ScoreTag
              className={styles.tag}
              score={scores[0]['Personality'] * 100}
              label={t`Personality`}
            />
          </button>
        </div>
      </Box>
      <Box className={styles.rightSection} direction="column">
        {noOutcomeFactors ? null : (
          <Box direction="column" className={styles.factors}>
            <div className={tabStyles.sectionTitle}>
              {t`Outcome factors`}
              <IconTooltip text={factorsOverview[language]} />
            </div>

            <div className={styles.factorsList}>
              {outcomeFactors.map(
                ({ label, score, isHighGood, description }) => (
                  <div key={label} className={styles.factor}>
                    <div className={styles.factorLabel}>
                      {label} <IconTooltip text={description[language]} />
                    </div>
                    <div
                      className={cx(styles.factorScore, {
                        [styles.bad]: isHighGood ? score <= 33 : score > 66,
                        [styles.medium]: score > 33 && score <= 66,
                        [styles.good]: isHighGood ? score > 66 : score <= 33,
                      })}
                    >
                      {getSoreLabel(Math.min(Math.floor(score / 33.3), 2))}
                    </div>
                  </div>
                ),
              )}
            </div>
          </Box>
        )}

        {noCategories ? undefined : (
          <Box
            className={cx(styles.profileMatch, {
              [styles.noPadding]: noOutcomeFactors,
            })}
            direction="column"
          >
            <div className={tabStyles.sectionTitle}>{t`Profile match`}</div>
            <div
              className={cx(
                tabStyles.sectionExplanations,
                tabStyles.withPadding,
              )}
            >{t`Estimated match for each competency`}</div>
            {showRadar ? (
              <RadarChart
                data={radarData}
                single={single}
                selectedCandidates={selectedCandidates}
                candidates={candidates}
                maxHeight={'20rem'}
                setSelected={setSelectedCompetency}
              />
            ) : (
              <div className={styles.donuts}>
                {visibleCategories[0].map((cat, index) => (
                  <div
                    key={cat.label}
                    className={cx(styles.donut, {
                      [styles.isSingle]: visibleCategories[0]?.length === 1,
                    })}
                  >
                    <div className={styles.title}>{cat.label}</div>
                    <DonutChart
                      scoreLabel={getScoreLabel(visibleCategories[0], index)}
                      isGreen={!!index}
                      value={cat.score * 25}
                    />
                  </div>
                ))}
              </div>
            )}
          </Box>
        )}
      </Box>
    </Box>
  )
}
